import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import {
  useAdminAccounts,
  type AdminAccountsParams,
  type AdminAccountSortBy,
} from "@/hooks/use-admin-accounts";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { useAdminAccountsColumns } from "@/components/accounts/admin-accounts-columns";
import {
  AdminAccountsFilters,
  type AdminAccountsFilterValues,
} from "@/components/accounts/admin-accounts-filters";

type Account = AdminComponents["schemas"]["Account"];

export const Route = createFileRoute("/_admin/accounts/")({
  validateSearch: (search: Record<string, unknown>) => ({
    org_id: typeof search.org_id === "string" ? search.org_id : undefined,
  }),
  component: AdminAccountsListPage,
});

const PAGE_SIZE = 20;

function AdminAccountsListPage() {
  const { t } = useTranslation(["accounts", "admin", "common"]);
  const navigate = useNavigate({ from: Route.fullPath });
  const { org_id: urlOrgId } = Route.useSearch();
  const columns = useAdminAccountsColumns();

  const [filters, setFilters] = useState<AdminAccountsFilterValues>(() =>
    urlOrgId ? { org_id: urlOrgId, status: "active" } : { status: "active" },
  );
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

  useEffect(() => {
    if ((filters.org_id ?? undefined) !== urlOrgId) {
      void navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          org_id: filters.org_id,
        }),
        replace: true,
      });
    }
  }, [filters.org_id, urlOrgId, navigate]);

  useEffect(() => {
    setPage(1);
  }, [filters, sorting]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as AdminAccountSortBy | undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  const params: AdminAccountsParams = useMemo(
    () => ({
      org_id: filters.org_id ?? "",
      account_type: filters.account_type,
      status: filters.status,
      name_search: filters.name_search,
      sort_by: sortBy,
      sort_dir: sortDir,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, sortBy, sortDir, page],
  );

  const { data, isLoading, isError } = useAdminAccounts(params);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = (row: Account) => {
    void navigate({ to: "/accounts/$id", params: { id: row.id } });
  };

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">{t("list.title")}</h1>
        <p className="text-sm text-slate-500">
          {t("admin:cross_org_browse", {
            defaultValue: "Cross-org read-only browse via the admin API.",
          })}
        </p>
      </header>

      <AdminAccountsFilters filters={filters} onChange={setFilters} />

      {!filters.org_id ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          {t("admin:pick_org_first", {
            defaultValue: "Pick an organization above to load its accounts.",
          })}
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {t("errors.loadFailed")}
        </div>
      ) : isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          {t("list.empty")}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={handleRowClick}
            sorting={sorting}
            onSortingChange={setSorting}
          />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
